/**
 * Plans and payments (paywall plan P3). Planning alone never touches this file; it's used only by the "plan together"
 * UI (Plans dialog, Plan & billing in the account menu) and when Stripe sends someone back to the app.
 *
 * Free trial (owner's decision, 2026-11): Cloud and Team start with 7 free days through Stripe Checkout (card at
 * checkout, charged on day 8 unless cancelled; `trial_days` per price, `trial.available` per person, once per account,
 * any plan). Lifetime is bought at once. Older servers without `trial_days` still have the card-free trial
 * (POST /api/billing/trial, startTrialAndResume).
 *
 * Checkout, receipts, refunds and the card live on Stripe's pages (Stripe Managed Payments: "Sold through Link"). The
 * server (apps/server/src/billing.ts) is the only gate; this file only asks it and shows the answers.
 */
import { create } from "zustand";
import { api, ApiError, myWorlds, type MyWorld } from "./collab.ts";
import { isDesktop, onDeepLink, openExternal } from "./desktop.ts";
import { useAccount } from "./auth.ts";

export type PaidPlan = "cloud" | "team" | "lifetime";

export interface PlanRow {
  id: string;
  name: string;
  rank: number;
  max_worlds: number;
  max_members: number;
  storage_bytes: number;
  history_days: number;
  allow_admins: boolean;
  self_host: boolean;
}
export interface PlanPrice {
  plan: PaidPlan;
  price_id: string;
  amount: number;
  currency: string;
  currency_options: Record<string, number>;
  interval: "month" | null;
  /**
   * free days through Stripe Checkout before the first charge (card at checkout, charged on day 8 unless cancelled):
   * 7 for Cloud and Team, null for Lifetime. Missing on servers from before the Checkout trial (2026-11).
   */
  trial_days?: number | null;
}
export interface Entitlement {
  plan: string;
  name: string;
  source: "free" | "beta" | "grant" | "subscription" | "lifetime" | "trial" | string;
  expires_at: string | null;
  renews_at: string | null;
  max_worlds: number;
  max_members: number;
  storage_bytes: number;
  allow_admins: boolean;
  self_host: boolean;
}
export interface BillingStatus {
  plan: Entitlement;
  subscription: {
    id: string; plan: string; status: string; cancel_at_period_end: boolean; cancel_at: string | null; current_period_end: string | null; currency: string | null;
    /** while status is "trialing": when the free days end and the first payment is due */
    trial_end?: string | null;
  } | null;
  lifetime: { issued_at: string } | null;
  /** `available`: this person may still start a free trial (once, any plan); `days`: how long it is */
  trial: { started_at: string | null; ends_at: string | null; available: boolean; days?: number };
  customer: boolean;
  usage: { worlds: number; storage_bytes: number };
  enforced: boolean;
  payments: boolean;
}

interface BillingState {
  /** the Plans dialog: why it opened (e.g. "Sharing needs a plan"), and what to carry on with once there's a plan */
  dialog: { open: boolean; reason?: string; resume?: () => void };
  plans: PlanRow[];
  prices: PlanPrice[];
  status: BillingStatus | null;
  /** back from Stripe Checkout: waiting for the webhook to switch the plan on */
  /** `emailing`: the server will email the person once the plan is on (slow Checkout, notify-when-active) */
  activating: { plan: PaidPlan | null; done: boolean; slow: boolean; emailing?: boolean } | null;
  /** desktop app: Checkout is open in the system browser; the app watches for the new plan meanwhile */
  waiting: { plan: PaidPlan; url: string; since: number; resume?: () => void } | null;
  /** the "Choose which worlds stay live" dialog */
  liveDialog: boolean;
  /** the currency picked in the Plans dialog (null: the visitor's own, localCurrency) */
  currency: string | null;
  busy: string | null;
  error: string | null;
  set: (p: Partial<BillingState>) => void;
}

export const useBilling = create<BillingState>((set) => ({
  dialog: { open: false },
  plans: [],
  prices: [],
  status: null,
  activating: null,
  waiting: null,
  liveDialog: false,
  currency: null,
  busy: null,
  error: null,
  set: (p) => set(p),
}));

// ---------------- server calls ----------------

export async function loadPlans(server: string) {
  const r = await api<{ plans: PlanRow[]; prices: PlanPrice[]; enforced: boolean; payments: boolean }>(server, "/api/plans", { anonymous: true });
  // a captive portal, a proxy or a static host can answer 200 with something else: never put that in the store
  if (!Array.isArray(r?.plans) || !Array.isArray(r?.prices)) throw new Error("The plans didn't load. Try again in a moment.");
  useBilling.getState().set({ plans: r.plans, prices: r.prices });
  return r;
}

/** Servers since the Checkout trial (2026-11) say per price how many free days it has; older ones start a trial without a card. */
export const checkoutTrials = (prices: PlanPrice[]) => prices.some((p) => "trial_days" in p);
/** free days `plan` starts with through Checkout (0: none, or an older server) */
export const trialDays = (prices: PlanPrice[], plan: PaidPlan) => prices.find((p) => p.plan === plan)?.trial_days ?? 0;

export async function refreshBilling(server: string) {
  const status = await api<BillingStatus>(server, "/api/billing");
  if (!status?.plan || !status.trial || !status.usage) throw new Error("Your plan didn't load. Try again in a moment.");
  useBilling.getState().set({ status });
  return status;
}

async function run<T>(what: string, f: () => Promise<T>): Promise<T | null> {
  const b = useBilling.getState();
  b.set({ busy: what, error: null });
  try {
    return await f();
  } catch (e) {
    useBilling.getState().set({ error: (e as Error).message });
    return null;
  } finally {
    useBilling.getState().set({ busy: null });
  }
}

export const startTrial = (server: string) =>
  run("trial", async () => {
    const status = await api<BillingStatus>(server, "/api/billing/trial", { method: "POST" });
    useBilling.getState().set({ status });
    return status;
  });

/**
 * Off to Stripe's checkout page. Web: this tab (Stripe sends people back to #billing=success). Desktop: the system
 * browser — never inside the app — and the app watches for the new plan meanwhile (Stripe sends people to
 * /desktop-return.html, which opens the app again with mcplanner://billing/success).
 * `trial`: start with the plan's free days (Cloud, Team; Stripe asks for the card, the first charge comes after them,
 * cancelling during them costs nothing). The server answers 409 trial_used when this person has had a trial (the plan
 * then shows its normal price, the person picks again), 503 trial_unavailable while trials aren't switched on (said,
 * never charged instead). Stripe picks the currency itself (Adaptive Pricing); the dialog's € £ $ only shows prices.
 */
export const checkout = (server: string, plan: PaidPlan, opts: { trial?: boolean } = {}) =>
  run(`checkout:${plan}`, async () => {
    const desktop = isDesktop();
    let url: string;
    try {
      ({ url } = await api<{ url: string }>(server, "/api/billing/checkout", {
        method: "POST", body: JSON.stringify({ plan, client: desktop ? "desktop" : undefined, ...(opts.trial ? { trial: true } : null) }),
      }));
    } catch (e) {
      if (e instanceof ApiError && e.code === "trial_used") await refreshBilling(server).catch(() => null);
      throw new Error(checkoutMessage(e));
    }
    if (desktop) {
      const resume = useBilling.getState().dialog.resume;
      useBilling.getState().set({ waiting: { plan, url, since: Date.now(), resume }, dialog: { open: false } });
      await openExternal(url);
      void watchDesktopCheckout(server);
    } else {
      location.assign(url);
    }
    return url;
  });

/** The server's checkout refusals in plain words (its `error` may be a bare code like "trial_used"). */
function checkoutMessage(e: unknown) {
  const err = e as ApiError;
  const msg = err?.message ?? String(e);
  const bare = /^[a-z_]+$/.test(msg);
  if (err?.code === "trial_used") return bare ? "You've already had your free trial, so this plan starts at its normal price." : msg;
  if (err?.code === "trial_unavailable") return bare ? "Free trials aren't switched on yet. Please try again later." : msg;
  if (err?.code === "bad_trial") return "Lifetime is bought at once, without free days.";
  if (err?.status === 429) return "Too many tries. Wait a minute.";
  return bare ? "Checkout didn't open. Please try again in a moment." : msg;
}

export const changePlan = (server: string, plan: "cloud" | "team") =>
  run(`change:${plan}`, async () => {
    const status = await api<BillingStatus>(server, "/api/billing/change", { method: "POST", body: JSON.stringify({ plan }) });
    useBilling.getState().set({ status });
    return status;
  });

export const cancelSubscription = (server: string, resume = false) =>
  run(resume ? "resume" : "cancel", async () => {
    const status = await api<BillingStatus>(server, resume ? "/api/billing/resume" : "/api/billing/cancel", { method: "POST" });
    useBilling.getState().set({ status });
    return status;
  });

/** Stripe's page for invoices, the card and the billing address. */
export const openPortal = (server: string) =>
  run("portal", async () => {
    const { url } = await api<{ url: string }>(server, "/api/billing/portal", { method: "POST", body: JSON.stringify({ client: isDesktop() ? "desktop" : undefined }) });
    if (isDesktop()) await openExternal(url);
    else location.assign(url);
    return url;
  });

/** The Plans dialog. `resume`: what the person was doing (e.g. going live) — carried on once they have a plan. */
export function openPlans(reason?: string, resume?: () => void) {
  useBilling.getState().set({ dialog: { open: true, reason, resume }, error: null });
}
export const closePlans = () => useBilling.getState().set({ dialog: { open: false } });

/** "Start free trial" in the Plans dialog: closes it and carries on with what the person was doing. */
export async function startTrialAndResume(server: string) {
  const resume = useBilling.getState().dialog.resume;
  const st = await startTrial(server);
  if (!st) return false;
  closePlans();
  resume?.();
  return true;
}

// ---------------- which worlds stay live (P4) ----------------

/** The worlds this person owns, for "Choose which worlds stay live". */
export async function ownedWorlds(server: string): Promise<MyWorld[]> {
  return (await myWorlds(server)).filter((w) => w.role === "owner");
}

export const setLiveWorlds = (server: string, rooms: string[]) =>
  run("live", async () => {
    const r = await api<{ worlds: MyWorld[]; changed: string[] }>(server, "/api/worlds/live", { method: "POST", body: JSON.stringify({ rooms }) });
    await refreshBilling(server).catch(() => null);
    return r;
  });

export const openLiveWorlds = () => useBilling.getState().set({ liveDialog: true, error: null });

/** A refused "Share this world" (world_limit) → the Plans dialog, with the trial if it's still available. */
export function isPlanLimit(e: unknown): e is ApiError {
  return e instanceof ApiError && e.code === "world_limit";
}

// ---------------- links into the Plans dialog ----------------

/**
 * `#plans` opens the Plans dialog — the Minecraft mod links here as `#plans&ref=mod` ("Start 7 days free" when plan
 * files need a plan of your own). Returns where the link came from (`ref`), or null for any other address.
 */
export function parsePlansLink(hash = location.hash): { ref: string | null } | null {
  const m = /^#plans(?:&(.*))?$/.exec(hash);
  if (!m) return null;
  const ref = new URLSearchParams(m[1] ?? "").get("ref");
  return { ref: ref && /^[a-z0-9_-]{1,32}$/i.test(ref) ? ref : null };
}

/** the reason shown in the Plans dialog for a link from `ref` */
export const plansLinkReason = (ref: string | null) =>
  ref === "mod" ? "The Minecraft mod works with Cloud, Team, Lifetime and the 7-day trial — your own plan files included." : undefined;

// ---------------- back from Stripe ----------------

/** `#billing=success&plan=cloud&session=…`, `#billing=cancel`, `#billing=portal` */
export function parseBillingReturn(hash = location.hash): { kind: "success" | "cancel" | "portal"; plan: PaidPlan | null } | null {
  const m = /^#billing=(success|cancel|portal)(?:&(.*))?$/.exec(hash);
  if (!m) return null;
  const q = new URLSearchParams(m[2] ?? "");
  const plan = q.get("plan");
  return { kind: m[1] as "success" | "cancel" | "portal", plan: plan === "cloud" || plan === "team" || plan === "lifetime" ? plan : null };
}

/** has the plan that was bought switched on? (Lifetime bought while on Team: Team stays, Lifetime shows as owned) */
export const covers = (st: BillingStatus, want: PaidPlan | null) => {
  const got = st.plan.plan;
  if (want === "lifetime") return !!st.lifetime;
  if (want === null) return got !== "free" && got !== "trial";
  return got === want || (want === "cloud" && (got === "team" || got === "lifetime"));
};

/**
 * Stripe sent the person back. The webhook usually lands within seconds; until then "Activating…" (polls /api/billing
 * for up to 30 s, then says it's taking longer — the plan still switches on when the webhook arrives).
 */
export async function handleBillingReturn(server: string, hash = location.hash, pollMs = 1500, maxMs = 30_000) {
  const r = parseBillingReturn(hash);
  if (!r) return false;
  if (hash === location.hash) history.replaceState(null, "", location.pathname + location.search);
  const b = useBilling.getState();
  if (r.kind === "cancel") {
    b.set({ error: null, activating: null });
    return true;
  }
  if (r.kind === "portal") {
    await refreshBilling(server).catch(() => null);
    return true;
  }
  b.set({ activating: { plan: r.plan, done: false, slow: false } });
  const started = Date.now();
  for (;;) {
    const st = await refreshBilling(server).catch(() => null);
    if (st && covers(st, r.plan)) {
      useBilling.getState().set({ activating: { plan: r.plan, done: true, slow: false } });
      return true;
    }
    if (Date.now() - started > maxMs) {
      // the server emails them once the plan is on (only when its mail sender is on), or it's on by now
      const n = r.plan
        ? await api<{ active: boolean; emailing?: boolean }>(server, "/api/billing/notify-when-active", { method: "POST", body: JSON.stringify({ plan: r.plan }) }).catch(() => null)
        : null;
      if (n?.active) {
        await refreshBilling(server).catch(() => null);
        useBilling.getState().set({ activating: { plan: r.plan, done: true, slow: false } });
      } else useBilling.getState().set({ activating: { plan: r.plan, done: false, slow: true, emailing: !!n?.emailing } });
      return true;
    }
    await new Promise((res) => setTimeout(res, pollMs));
  }
}

// ---------------- desktop: Checkout in the system browser ----------------

let watching = false;
/**
 * While Checkout is open in the browser, look for the new plan every few seconds (and whenever the app gets focus).
 * When it's there: "You're on Cloud" and carry on with what the person was doing. Gives up after 30 minutes.
 */
export async function watchDesktopCheckout(server: string, everyMs = 3000, maxMs = 30 * 60_000) {
  if (watching) return;
  watching = true;
  try {
    for (;;) {
      const w = useBilling.getState().waiting;
      if (!w) return;
      const st = await refreshBilling(server).catch(() => null);
      if (st && covers(st, w.plan)) {
        useBilling.getState().set({ waiting: null, activating: { plan: w.plan, done: true, slow: false } });
        w.resume?.();
        return;
      }
      if (Date.now() - w.since > maxMs) { useBilling.getState().set({ waiting: null }); return; }
      await new Promise((res) => {
        const t = setTimeout(res, everyMs);
        const now = () => { clearTimeout(t); window.removeEventListener("focus", now); res(null); };
        window.addEventListener("focus", now);
      });
    }
  } finally {
    watching = false;
  }
}

/** `mcplanner://billing/success?plan=cloud` (from /desktop-return.html) → the same as #billing=success&plan=cloud */
export function billingLinkToHash(url: string): string | null {
  const m = /^mcplanner:\/\/billing\/(success|cancel|portal)(?:\?plan=(cloud|team|lifetime))?\/?$/.exec(url.trim());
  if (!m) return null;
  return `#billing=${m[1]}${m[2] ? `&plan=${m[2]}` : ""}`;
}

export async function handleBillingLink(server: string, url: string) {
  const hash = billingLinkToHash(url);
  if (!hash) return false;
  const kind = hash.slice("#billing=".length).split("&")[0];
  if (kind === "cancel") { useBilling.getState().set({ waiting: null }); return true; }
  if (kind === "success" && useBilling.getState().waiting) { void watchDesktopCheckout(server, 1500); return true; }
  return handleBillingReturn(server, hash);
}

let initialised = false;
/**
 * Once, at start-up: keep the signed-in person's plan fresh for the banners (after signing in, and when the app comes
 * back into focus, at most once a minute), and on the desktop listen for mcplanner://billing/… links.
 * Makes no request at all for someone who isn't signed in.
 */
export function initBilling(server: () => string) {
  if (initialised) return;
  initialised = true;
  let last = 0;
  const refresh = (force = false) => {
    if (useAccount.getState().status !== "signed-in") return;
    if (!force && Date.now() - last < 60_000) return;
    last = Date.now();
    void refreshBilling(server()).catch(() => null);
  };
  useAccount.subscribe((a, prev) => {
    if (a.status === "signed-in" && prev.status !== "signed-in") refresh(true);
    if (a.status === "signed-out" && prev.status === "signed-in") useBilling.getState().set({ status: null, waiting: null });
  });
  refresh(true);
  window.addEventListener("focus", () => refresh());
  if (isDesktop()) onDeepLink((url) => { void handleBillingLink(server(), url); });
}

// ---------------- showing prices ----------------

/** The currency Checkout will most likely show: £ in the UK, $ in the US, € elsewhere (Stripe converts the rest). */
export function localCurrency(p: PlanPrice, lang = typeof navigator === "undefined" ? "en" : navigator.language): { amount: number; currency: string } {
  const region = /[-_]([A-Za-z]{2})$/.exec(lang)?.[1]?.toUpperCase();
  const want = region === "GB" ? "gbp" : region === "US" ? "usd" : null;
  if (want && p.currency_options[want] !== undefined) return { amount: p.currency_options[want], currency: want };
  return { amount: p.amount, currency: p.currency };
}

/** a price in the currency picked in the Plans dialog (its own currency when it has no fixed price in that one) */
export function priceIn(p: PlanPrice, currency: string | null): { amount: number; currency: string } {
  if (!currency || currency === p.currency) return { amount: p.amount, currency: p.currency };
  if (p.currency_options[currency] !== undefined) return { amount: p.currency_options[currency], currency };
  return { amount: p.amount, currency: p.currency };
}

/** the currencies the Plans dialog offers (€ £ $, as far as the prices have them) */
export function currencies(prices: PlanPrice[]): string[] {
  const have = new Set(prices.flatMap((p) => [p.currency, ...Object.keys(p.currency_options)]));
  return ["eur", "gbp", "usd"].filter((c) => have.has(c));
}

export function fmtPrice(amount: number, currency: string, lang = typeof navigator === "undefined" ? "en" : navigator.language) {
  try {
    return new Intl.NumberFormat(lang, { style: "currency", currency: currency.toUpperCase() }).format(amount / 100);
  } catch {
    return `${(amount / 100).toFixed(2)} ${currency.toUpperCase()}`;
  }
}

export function fmtDate(iso: string | null | undefined) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); } catch { return iso; }
}

export const daysLeft = (iso: string | null | undefined, now = Date.now()) => (iso ? Math.max(0, Math.ceil((Date.parse(iso) - now) / 86400_000)) : 0);
